import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useISPowner from "../../../../hooks/useISPOwner";
import useSelectorState from "../../../../hooks/useSelectorState";
import ComponentCustomModal from "../../../../components/common/customModal/ComponentCustomModal";
import { Form, Formik } from "formik";
import { FtextField } from "../../../../components/common/FtextField";
import Table from "../../../../components/table/Table";
import Loader from "../../../../components/common/Loader";
import useDataInputOption from "../../../../hooks/useDataInputOption";
import {
  getCustomerDayLeft,
  getMonthStartDay,
} from "../customerBillDayPromiseDate";
import { Trash } from "react-bootstrap-icons";
import { bulkPackageEdit } from "../../../../features/actions/bulkOperationApi";
import { adminResellerCommission } from "../../../reseller/resellerCollection/CommissionShear";
import useDataState from "../../../../hooks/useDataState";

const BulkPackageUpdate = ({ show, setShow, page, bulkCustomer }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const today = new Date();

  // get user & current user data form useISPOwner hook
  const { role, hasMikrotik, userData, permissions, permission } =
    useISPowner();

  // get user data set from useDataState hooks
  const { formData, setFormData } = useDataState();

  const adminUser =
    role === "ispOwner" ||
    role === "manager" ||
    (role === "collector" && !userData.reseller);

  //---> Get redux store state data from useSelectorState hooks
  const { allPackages } = useSelectorState();

  // get mikrotik and without mikrotik packages in redux store
  const packages = useSelector((state) =>
    page === "pppoe"
      ? hasMikrotik
        ? adminUser
          ? state?.package?.allPackages
          : state?.mikrotik?.pppoePackage
        : state?.mikrotik?.pppoePackage
      : page === "static"
        ? hasMikrotik
          ? adminUser
            ? state?.package?.packages
            : state?.mikrotik?.pppoePackage
          : state?.package?.packages
        : state.hotspot.package,
  );

  const extraInputOptions = [
    {
      name: "updatePackage",
      as: "select",
      type: "select",
      id: "updatePackage",
      isVisible: true,
      disabled: false,
      validation: true,
      label: t("updatePackage"),
      firstOptions: t("selectPackage"),
      textAccessor: "name",
      valueAccessor: "id",
      options:
        page === "pppoe"
          ? hasMikrotik
            ? adminUser
              ? packages?.filter(
                  (pack) =>
                    pack.packageType === "pppoe" &&
                    pack.mikrotik === formData.mikrotikId,
                )
              : packages?.map((pack) => ({
                  ...pack,
                  disabled:
                    pack.packageType === "pppoe" &&
                    pack.mikrotik === formData.mikrotikId &&
                    pack.rate <= formData.packageRate,
                }))
            : packages
          : packages?.filter((pack) => pack.packageType === "queue"),
    },
  ];

  // call the data input option function
  const inputPermission = {
    mikrotik: true,
    mikrotikPackage: true,
    downloadPackage: true,
    updatePackage: true,
  };

  // get data input option from useDataInputOption hook
  const dataInputOption = useDataInputOption(
    inputPermission,
    page,
    "bulk",
    null,
    extraInputOptions,
    formData,
    setFormData,
  );

  const visibleInputFields = useMemo(() => {
    if (!dataInputOption?.inputOption) return [];

    const optionMap = dataInputOption.inputOption.reduce((acc, option) => {
      acc[option.name] = option;
      return acc;
    }, {});

    return Object.keys(inputPermission)
      .map((field) => optionMap[field])
      .filter((item) => item && item.isVisible);
  }, [dataInputOption, inputPermission]);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [selectMikroTik, setSelectMikroTik] = useState(null);
  const [selectMikroTikPackage, setSelectMikroTikPackage] = useState(null);
  const [selectUpdatePackage, setSelectUpdatePackage] = useState(null);

  const customers = useMemo(() => {
    if (!bulkCustomer) return [];

    return bulkCustomer
      .filter((item) => item.original.monthlyFee > 0)
      .map((item) => ({
        ...item.original,
        amount: item.original.monthlyFee,
        newUploadPackage: item.original.uploadPackage,
      }));
  }, [bulkCustomer]);

  const calculateCustomerBalance = (customer, packageRate, packageId) => {
    const billingDate = new Date(customer?.billingCycle);

    const monthDate = new Date(
      billingDate.getFullYear(),
      billingDate.getMonth(),
      0,
    ).getDate();

    const monthDay = new Date(
      billingDate.getFullYear(),
      billingDate.getMonth() - 1,
      billingDate.getDate(),
    );

    // customer useds day in current date
    const daysUsed = getMonthStartDay(monthDay);

    // customer bill day left
    const dayLeft = getCustomerDayLeft(customer.billingCycle);

    // customer current package useds day amount
    const currentPackgeUsedDayAmount =
      (customer.monthlyFee / monthDate) * daysUsed;

    // customer current package day left amount
    const currentPackgeBalance =
      customer.monthlyFee - currentPackgeUsedDayAmount;

    // customer day left amountcurrentPackgeBalance
    const changePackageDayLeftAmount = (packageRate / monthDate) * dayLeft;

    // customer change package amount after day left change in current date
    let changePackageAmount = 0;

    if (customer.status === "active" && billingDate >= today && customer) {
      // customer current package change before current package and after balance
      if (customer.mikrotikPackage === packageId) {
        changePackageAmount = customer.balance;
      } else {
        if (customer.balance < 0) {
          // customer privious package and crrent package used amount
          changePackageAmount = -(
            currentPackgeUsedDayAmount +
            changePackageDayLeftAmount -
            customer.balance
          );
        } else {
          changePackageAmount =
            currentPackgeBalance -
            changePackageDayLeftAmount +
            customer.balance;
        }
      }
    } else {
      // for inactive or expired user change package monthly fee
      changePackageAmount = -packageRate + customer.balance;
    }

    const { ispOwnerCommission = 0 } =
      role === "reseller"
        ? adminResellerCommission(
            userData,
            {
              pageStatus: "edit",
              amount: packageRate,
              mikrotikPackage: packageId,
            },
            role,
          )
        : {};

    return {
      ...customer,
      newMikroTikPackage: packageId,
      newDownloadPackage: packageId,
      newUploadPackage: customer.uploadPackage,
      newMonthlyFee: packageRate,
      newBalance: Math.trunc(changePackageAmount),
      ispOwnerCommission,
    };
  };

  const compareCustomers = (customers, mtkPackage) => {
    return customers.map((customer) =>
      calculateCustomerBalance(customer, mtkPackage?.rate, mtkPackage?.id),
    );
  };

  const updateCustomers = useMemo(() => {
    let result = [...customers];

    if (selectMikroTik) {
      result = result.filter(
        (customer) => customer.mikrotik === selectMikroTik,
      );
    }

    if (selectMikroTikPackage) {
      result = result.filter(
        (customer) => customer.mikrotikPackage === selectMikroTikPackage,
      );
    }

    if (selectUpdatePackage) {
      result = compareCustomers(result, selectUpdatePackage);
    }

    return result;
  }, [customers, selectMikroTik, selectMikroTikPackage, selectUpdatePackage]);

  const handleFormValue = (event, values, setFieldValue) => {
    if (event.target.name === "mikrotik") {
      setSelectMikroTik(event.target.value);
    }

    if (
      event.target.name === "mikrotikPackage" ||
      event.target.name === "downloadPackage"
    ) {
      setSelectMikroTikPackage(event.target.value);
    }

    if (event.target.name === "updatePackage") {
      const mtkPackage = packages?.find(
        (pack) => pack.id === event.target.value,
      );

      setFieldValue("updatePackage", event.target.value);
      setSelectUpdatePackage(mtkPackage);
    }

    const newMonthlyFee = event.target.name.match(
      /^customers\.(\d+)\.newMonthlyFee$/,
    );

    if (newMonthlyFee) {
      const index = Number(newMonthlyFee[1]);
      const customer = values.customers[index];

      if (event.target.value >= 0) {
        const updatedCustomer = calculateCustomerBalance(
          customer,
          Number(event.target.value),
          selectUpdatePackage?.id,
        );

        setFieldValue(`customers.${index}`, updatedCustomer);
      }
    }

    const newUploadPackage = event.target.name.match(
      /^customers\.(\d+)\.newUploadPackage$/,
    );

    if (newUploadPackage) {
      const index = Number(newUploadPackage[1]);

      setFieldValue(`customers.${index}.newUploadPackage`, event.target.value);
    }
  };

  // const removeSelectedUserFromBulkPackageUpdateOptions = (
  //   id,
  //   values,
  //   setFieldValue,
  // ) => {
  //   const remainingCustomers = values.customers.filter(
  //     (customer) => customer.id !== id,
  //   );

  //   setFieldValue("customers", remainingCustomers);
  // };

  const customerPackageFind = (pack) => {
    const findPack = packages.find((item) => item.id.includes(pack));

    return findPack;
  };

  const customerBulkPackageUpdateHandler = (formValues) => {
    const sendingData = {
      mikrotik: formValues.mikrotik,
      mikrotikPackage: formValues.updatePackage,

      ...(page === "static" && {
        downloadPackage: formValues.updatePackage,
      }),

      customers: formValues.customers.map((customer) => ({
        id: customer.id,
        monthlyFee: customer.newMonthlyFee,
        balance: customer.newBalance,
        uploadPackage: customer.newUploadPackage,
      })),
    };

    bulkPackageEdit(dispatch, sendingData, setIsLoading, setShow);
  };

  const columns = useMemo(
    () => [
      {
        width: "15%",
        Header: t("pppoeIp"),
        accessor: (field) =>
          `${field?.name} ${
            field?.userType === "pppoe"
              ? field?.pppoe?.name
              : field?.userType === "static"
                ? field?.queue?.target
                : field?.hotspot?.name
          }`,
        Cell: ({ row: { original } }) => {
          const name = original?.name || original?.name;

          let addressInfo = "";
          if (original?.userType === "pppoe") {
            addressInfo = original?.pppoe?.name;
          } else if (original?.userType === "static") {
            addressInfo = original?.queue?.target;
          } else {
            addressInfo = original?.hotspot?.name;
          }

          return (
            <div>
              <p>{name}</p>
              <p>{addressInfo}</p>
            </div>
          );
        },
      },
      {
        width: "15%",
        Header: t("packageBill"),
        accessor: (field) =>
          field.customer?.userType === "pppoe"
            ? field.customer?.pppoe?.profile
            : customerPackageFind(
                field?.uploadPackage
                  ? field?.uploadPackage
                  : field?.mikrotikPackage,
              )?.name,
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p>
                {original?.userType === "pppoe" ? (
                  original?.pppoe.profile
                ) : (
                  <p>
                    {
                      customerPackageFind(
                        original?.uploadPackage
                          ? original?.uploadPackage
                          : original?.mikrotikPackage,
                      )?.name
                    }
                    /
                    {
                      customerPackageFind(
                        original?.downloadPackage
                          ? original?.downloadPackage
                          : original?.mikrotikPackage,
                      )?.name
                    }
                  </p>
                )}
              </p>
              <p style={{ fontWeight: "500" }}>৳{original?.monthlyFee}</p>
            </div>
          );
        },
      },
      {
        width: "10%",
        Header: t("balance"),
        accessor: "balance",
      },
      {
        width: "20%",
        Header: t("updatePackage") + " & " + t("bill"),
        accessor: (field) =>
          field?.userType === "pppoe"
            ? customerPackageFind(field.newMikroTikPackage)
            : customerPackageFind(
                field?.newDownloadPackage
                  ? field?.newDownloadPackage
                  : field?.newMikroTikPackage,
              )?.name,
        Cell: ({ row, cell }) => {
          const customer = row?.original;

          return (
            <div>
              <div>
                {customer?.userType === "pppoe" ? (
                  <p>
                    {customerPackageFind(customer?.newMikroTikPackage)?.name ??
                      "N/A"}
                  </p>
                ) : (
                  <p>
                    {customerPackageFind(
                      customer?.newUploadPackage
                        ? customer?.newUploadPackage
                        : customer?.newUploadPackage,
                    )?.name ?? "N/A "}
                    /
                    {customerPackageFind(
                      customer?.newDownloadPackage
                        ? customer?.newDownloadPackage
                        : customer?.newDownloadPackage,
                    )?.name ?? " N/A"}
                  </p>
                )}{" "}
              </div>

              <div className="d-flex align-items-center gap-1 mt-0">
                <div className="fs-5">৳</div>
                <div>
                  <FtextField
                    name={`customers.${row.index}.newMonthlyFee`}
                    type="number"
                    min={customer.ispOwnerCommission}
                    disabled={
                      adminUser
                        ? role !== "ispOwner" &&
                          !(
                            permissions?.customerEdit ||
                            permissions?.monthlyFeeEdit
                          )
                        : !(
                            (permission?.customerEdit &&
                              permission?.monthlyFeeEdit) ||
                            permission?.monthlyFeeEdit
                          )
                    }
                  />
                </div>
              </div>
            </div>
          );
        },
      },
      {
        width: "10%",
        Header: t("balance"),
        accessor: "newBalance",
      },
      ...(page === "static"
        ? [
            {
              width: "15%",
              Header: t("uploadPackge"),
              accessor: "uploadPackage",
              Cell: ({ row, cell }) => (
                <FtextField
                  {...{
                    as: "select",
                    name: `customers.${row.index}.newUploadPackage`,
                    type: "select",
                    id: "newUploadPackage",
                    isVisible: true,
                    disabled: false,
                    validation: false,
                    firstOptions: t("selectPackage"),
                    textAccessor: "name",
                    valueAccessor: "id",
                    options:
                      page === "pppoe"
                        ? hasMikrotik
                          ? packages?.filter(
                              (pack) => pack.packageType === "pppoe",
                            )
                          : packages
                        : packages?.filter(
                            (pack) => pack.packageType === "queue",
                          ),
                  }}
                />
              ),
            },
          ]
        : []),
      // {
      //   width: "5%",
      //   Header: t("action"),
      //   id: "option",
      //   Cell: ({ row: { original } }) => (
      //     <div className="d-flex justify-content-center align-items-center">
      //       <button
      //         type="button"
      //         className="btn btn-sm text-danger text-center"
      //         onClick={() =>
      //           removeSelectedUserFromBulkPackageUpdateOptions(
      //             original.id,
      //             values,
      //             setFieldValue,
      //           )
      //         }
      //       >
      //         <Trash size={18} />
      //       </button>
      //     </div>
      //   ),
      // },
    ],
    [t, allPackages],
  );

  return (
    <ComponentCustomModal
      size="xl"
      show={show}
      setShow={setShow}
      header={t("bulkPackageUpdate")}
    >
      <Formik
        enableReinitialize
        initialValues={{
          ...dataInputOption?.inputInitialValues,
          customers: updateCustomers,
        }}
        onSubmit={(values) => {
          customerBulkPackageUpdateHandler(values);
        }}
      >
        {({ values, setFieldValue }) => (
          <Form
            onChange={(event) => handleFormValue(event, values, setFieldValue)}
            id="packageUpdate"
          >
            <div className="displayGrid3">
              {visibleInputFields?.map(
                (item) =>
                  item?.isVisible && <FtextField key={item.name} {...item} />,
              )}
            </div>

            <div className="collectorWrapper pb-2">
              <Table columns={columns} data={values.customers} />
            </div>
          </Form>
        )}
      </Formik>

      <div className="modal-footer" style={{ border: "none" }}>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={isLoading}
          onClick={() => setShow(false)}
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          className="btn btn-success"
          form="packageUpdate"
          disabled={isLoading}
        >
          {isLoading ? <Loader /> : t("update")}
        </button>
      </div>
    </ComponentCustomModal>
  );
};

export default BulkPackageUpdate;
